import { useState } from 'react';
import { useRouter } from 'next/router';

import {
  Alert,
  Button,
  ButtonGroup,
  Heading,
  Icon,
  List,
  LoadingMask,
  Modal,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconKeyFilled, IconYardSignFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyDetailsWebApiHandler_details,
  useRentalManagerUserPropertyWaitlistV2WebApiHandler_disable,
  useRentalManagerUserPropertyWaitlistV2WebApiHandler_enable,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';

const waitlistActionsCopy = {
  start: {
    icon: <IconKeyFilled />,
    heading: 'Start a waitlist?',
    title: 'Get leads next time you list your property',
    details: [
      { message: 'The listing will remain active, but the property will show as unavailable.', showUnpaid: true },
      {
        message:
          'Renters can indicate interest, and you’ll receive their contact information when you end the waitlist.',
        showUnpaid: true,
      },
      {
        message: 'Because the listing will remain active, the monthly fee for your package tier will still apply.',
        showUnpaid: false,
      },
    ],
    footer: 'Still have questions? Contact your Zillow representative.',
  },
  end: {
    icon: <IconYardSignFilled />,
    heading: 'End the waitlist?',
    title: 'When you end the waitlist',
    details: [
      { message: 'The listing will show as an available property on Zillow, Trulia & HotPads.', showUnpaid: true },
      {
        message: 'You’ll receive the list of any renters who showed interest in the property while it was unavailable.',
        showUnpaid: true,
      },
    ],
    footer: undefined,
  },
} as const;

type WaitlistModalProps = {
  setShowWaitlistModal: (show: boolean) => void;
};

export const WaitlistModal = ({ setShowWaitlistModal }: WaitlistModalProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const isWaitlisted = listingDetails?.isWaitlisted ?? false;
  const customerId = listingDetails?.adproduct?.isUnpaid
    ? listingDetails?.adproduct?.unpaidCustomerId
    : listingDetails?.customerId;
  const isUnpaid = listingDetails?.adproduct?.isUnpaid ?? false;

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [action] = useState<'start' | 'end'>(isWaitlisted ? 'end' : 'start');

  const { mutate: enableWaitlist, isPending: isEnabling } = useRentalManagerUserPropertyWaitlistV2WebApiHandler_enable({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'Enabling property waitlist',
      },
    },
    onSuccess: () => {
      setStep(2);
      queryClient.invalidateQueries({
        queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({ listingAlias }),
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Waitlist couldn’t be turned on. Try again.';
      setError(message);
    },
  });

  const { mutate: disableWaitlist, isPending: isDisabling } =
    useRentalManagerUserPropertyWaitlistV2WebApiHandler_disable({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'Disabling property waitlist',
        },
      },
      onSuccess: () => {
        setShowWaitlistModal(false);
        queryClient.invalidateQueries({
          queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({ listingAlias }),
        });
      },
      onError: (error: unknown) => {
        const message = error instanceof Error ? error.message : 'Waitlist couldn’t be turned off. Try again.';
        setError(message);
      },
    });

  const loading = isEnabling || isDisabling;

  const submitWaitlist = () => {
    if (!customerId) return;

    setError('');

    if (action === 'end') {
      disableWaitlist({
        postData: {
          listingAlias,
          customerId,
        },
      });
    } else {
      enableWaitlist({
        postData: {
          listingAlias,
          customerId,
        },
      });
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && setShowWaitlistModal(false)}
      header={<Heading level={2}>{waitlistActionsCopy[action].heading}</Heading>}
      body={
        <div data-testid="WaitlistModal">
          {error && (
            <Alert tone="critical" css={{ marginBottom: '800' }}>
              {error}
            </Alert>
          )}

          {step === 1 ? (
            <LoadingMask loading={loading}>
              <Icon render={waitlistActionsCopy[action].icon} size="xl" css={{ marginBottom: '400' }} />
              <Heading level={3} textStyle="responsive.heading-sm" css={{ marginBottom: '400' }}>
                {waitlistActionsCopy[action].title}
              </Heading>
              <List.Root>
                {waitlistActionsCopy[action].details.map(
                  (detail, index) =>
                    (!isUnpaid || detail.showUnpaid) && (
                      <List.Item key={`${action}Detail${index}`}>{detail.message}</List.Item>
                    ),
                )}
              </List.Root>
              {waitlistActionsCopy[action].footer && (
                <Paragraph css={{ marginTop: '600', marginBottom: '800' }}>
                  {waitlistActionsCopy[action].footer}
                </Paragraph>
              )}
            </LoadingMask>
          ) : (
            step === 2 &&
            action === 'start' && (
              <>
                <Icon
                  render={<IconCheckmarkCircleFilled />}
                  size="xl"
                  css={{ marginBottom: '600', color: 'icon.accent.brand.hero' }}
                />
                <Heading level={3} textStyle="heading-sm" css={{ marginBottom: '400' }}>
                  Your property will show as unavailable
                </Heading>
                <Paragraph css={{ marginBottom: '600' }}>
                  Renters interested in the property will be saved for you until the end of the waitlist.
                </Paragraph>
                <Paragraph>{waitlistActionsCopy.start.footer}</Paragraph>
              </>
            )
          )}
        </div>
      }
      footer={
        step === 2 ? (
          <Button onClick={() => setShowWaitlistModal(false)} tone="brand" emphasis="filled" type="button">
            OK
          </Button>
        ) : (
          <ButtonGroup aria-label="Waitlist action buttons">
            <TextButton onClick={() => setShowWaitlistModal(false)} type="button">
              Cancel
            </TextButton>
            <Button tone="brand" emphasis="filled" size="sm" onClick={submitWaitlist} type="button">
              {`Yes, ${action === 'start' ? 'start' : 'end'} waitlist`}
            </Button>
          </ButtonGroup>
        )
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default WaitlistModal;
